import { useMemo } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

import {
  setPage,
  setLimit,
  setTotal,
  setColumnVisibility,
  setFreebetTableFilters,
  setClearFreebetTableFilters,
  setShowRevokeFreebetModal,
  setSelectedFreebet,
} from '@Modal/reducers/freebets/freebetTableSlice';
import {
  useRevokeSpecificFreeBetMutation,
  PaginatedFreeBetsDocument,
} from '@Src/generated.graphql';

// Types
import { StoreInterface } from 'src/app/types';
import { FilterItemType } from '@Modal/reducers/types';
import { useSnackbar } from 'notistack';
import { FreebetItemDto } from '@contracts/api/dto/freebet/FreebetDto';

export const useFreebetsTable = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [revokeSpecificFreeBet] = useRevokeSpecificFreeBetMutation();
  const {
    limit,
    page,
    total,
    selectedFilters,
    columnVisibility,
    freebetTableFilters,
    columnVisibilityOptions,
    showRevokeFreebetModal,
    selectedFreebet,
  } = useSelector((state: StoreInterface) => state?.freebetsTable);

  const freebetsListFilters = useMemo(() => {
    const columnVisibilityStrArr = Array.from(Object.values(columnVisibility)).map((filt) => filt);
    return columnVisibilityStrArr as string[];
  }, [columnVisibility]);

  const handleApplyColumnVisibility = (column: string[]) => {
    const newVisibleColumns = [...freebetsListFilters, ...column];
    if (freebetsListFilters.filter((col) => column.includes(col)).length > 0) {
      const filteredNewVisibleColumns = freebetsListFilters.filter((col) => col !== column[0]);
      dispatch(setColumnVisibility(filteredNewVisibleColumns));
    } else {
      dispatch(setColumnVisibility(newVisibleColumns));
    }
  };

  const visiblityCustomizable = useMemo(
    () => ({
      value: columnVisibility,
      options: columnVisibilityOptions,
      apply: (value: string[]) => handleApplyColumnVisibility(value),
    }),
    [columnVisibility, columnVisibilityOptions]
  );

  const handleFilterSelect = (filter: FilterItemType) => {
    dispatch(setFreebetTableFilters(filter));
  };

  const handleClearFreebetTableFilters = () => {
    dispatch(setClearFreebetTableFilters());
  };

  const handleRevokeFreebetModal = (arg: boolean, freeBet?: FreebetItemDto) => {
    if (arg) {
      dispatch(setSelectedFreebet(freeBet || {}));
    } else {
      dispatch(setSelectedFreebet({}));
    }
    dispatch(setShowRevokeFreebetModal(arg));
  };

  const handleRevokeFreebet = async (freeBet: FreebetItemDto) => {
    try {
      await revokeSpecificFreeBet({
        variables: {
          input: {
            campaignId: freeBet.campaignId as string,
            freeBetId: freeBet._id,
            providerId: freeBet.providerId as string,
          },
        },
        refetchQueries: [
          {
            query: PaginatedFreeBetsDocument,
            variables: { limit, page, filter: freebetTableFilters, search: '' },
          },
        ],
      });

      enqueueSnackbar('Freebet was successfully revoked', {
        variant: 'success',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });

      dispatch(setShowRevokeFreebetModal(false));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      enqueueSnackbar(message, {
        variant: 'error',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  };

  return {
    // State
    limit,
    page,
    total,
    selectedFreebet,
    selectedFilters,
    columnVisibility,
    freebetTableFilters,
    freebetsListFilters,
    visiblityCustomizable,
    columnVisibilityOptions,
    showRevokeFreebetModal,
    // Actions
    setPage,
    setLimit,
    setTotal,
    setFreebetTableFilters,
    setClearFreebetTableFilters,
    // Handlers
    handleRevokeFreebet,
    handleFilterSelect,
    handleRevokeFreebetModal,
    handleApplyColumnVisibility,
    handleClearFreebetTableFilters,
  };
};
